'use client'

import type React from 'react'

import { useEffect, useRef, useState } from 'react'

import { cn } from '@/utils/shadcn/utils'

import {
  Dialog as BaseDialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './base'
import { ModalProvider } from './modal-context'

export interface DialogProps {
  title?: React.ReactNode
  mainContent: React.ReactNode
  description?: React.ReactNode
  footer?: React.ReactNode
  showCloseButton?: boolean
  onInteractOutside?: (event: Event) => void
  onEscapeKeyDown?: (event: KeyboardEvent) => void
  /** Used when the dialog is to be opened by clicking on another component */
  trigger?: React.ReactNode
  /** Used when programmatically opening or closing the dialog */
  open?: boolean
  /** Automatically called when programmatically opening or closing the dialog */
  onOpenChange?: (open: boolean) => void
  /** Height of the dialog content when the transition ends */
  transitionEndHeight?: number
  /** Condition to determine if the dialog should transition to the transitionEndHeight */
  transitionCondition?: boolean
  dialogContentClassName?: string
  dialogMainContentClassName?: string
  /** Whether to justify the footer to the end */
  footerJustifyEnd?: boolean
  /** Visually hide the title while keeping it accessible to screen readers */
  hideTitle?: boolean
}

/**
 * @deprecated Use `Dialog` from `@moment/design-system-ui/dialog` instead.
 */
// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function Dialog(props: DialogProps) {
  const [height, setHeight] = useState<number | 'auto'>('auto')
  const contentRef = useRef<HTMLDivElement>(null)

  // When the content inside the dialog changes, if the parent supplied a transitionEndHeight,
  // set the height of the dialog to the transitionEndHeight, otherwise set it to the content height.
  // This will then cause a smooth transition to the new height. We need to track the current height
  // of the content in a ref since, without it, the height is 'auto' and the transition will not work.
  // biome-ignore lint/correctness/useExhaustiveDependencies: Bulk suppression as part of biome migration
  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.clientHeight
      setHeight(
        props.transitionCondition
          ? (props.transitionEndHeight ?? 'auto')
          : contentHeight,
      )
    }
  }, [
    props.mainContent,
    props.title,
    props.description,
    props.footer,
    props.transitionEndHeight,
    props.transitionCondition,
  ])

  // Cleanup pointer-events when dialog closes
  // This fixes a Radix UI bug where nested Popovers (like Select dropdowns) don't cleanup `pointer-events: none` from <body/> when the parent Dialog closes while they're still open
  useEffect(() => {
    if (!props.open) {
      // delay to let animations complete
      const timeoutId = setTimeout(() => {
        // Only cleanup if no other Radix overlays are present
        const hasOtherOverlays = document.querySelector(
          '[data-radix-dialog-overlay], [data-radix-popover-content]:not([data-state="closed"])',
        )
        if (!hasOtherOverlays) {
          document.body.style.pointerEvents = ''
        }
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [props.open])

  const hasTrigger = !!props.trigger
  const hasOpenAndChange =
    props.open !== undefined && props.onOpenChange !== undefined
  if (hasTrigger === hasOpenAndChange) {
    throw new Error(
      'Dialog must have either trigger or both open and onOpenChange, but not both or neither',
    )
  }

  return (
    <BaseDialog open={props.open} onOpenChange={props.onOpenChange}>
      {props.trigger && <DialogTrigger asChild>{props.trigger}</DialogTrigger>}
      <DialogContent
        showCloseButton={props.showCloseButton}
        onInteractOutside={props.onInteractOutside}
        onEscapeKeyDown={props.onEscapeKeyDown}
        ref={contentRef}
        className={props.dialogContentClassName}
        style={{
          height: !props.transitionCondition ? 'auto' : height,
          transition: 'height 0.3s ease-in-out',
        }}
      >
        {(props.title || props.description) && (
          <DialogHeader className={props.hideTitle ? 'sr-only' : undefined}>
            <DialogTitle>{props.title}</DialogTitle>
            <DialogDescription>{props.description}</DialogDescription>
          </DialogHeader>
        )}
        <ModalProvider>
          <span
            className={cn('text-secondary', props.dialogMainContentClassName)}
          >
            {props.mainContent}
          </span>
          {props.footer && (
            <DialogFooter justifyEnd={props.footerJustifyEnd}>
              {props.footer}
            </DialogFooter>
          )}
        </ModalProvider>
      </DialogContent>
    </BaseDialog>
  )
}
